/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { presentUnexpectedFailure } from '../../../shared/sentry/presentUnexpectedFailure';
import { Text, TextInput, useWindowDimensions, View } from 'react-native';
import { nationalPartFromStored } from '@shared/phone';
import { spacing } from '../../../shared/theme/spacing'
import { ticketColors } from '../../../shared/theme/ticketColors';
import { parseDecimalInput } from '../../../shared/numeric/localeNumeric';
import { NumericTextInput } from '../../../shared/ui/NumericTextInput';
import { showError, showSuccess } from '../../../shared/ui/appToast';
import { createSupplier, setSupplierActive, updateSupplier, countSuppliersLocal, listSuppliersLocalPaged } from '../../infra/suppliersRepo'
import { CatalogDomainError, SupplierCodeDuplicateError } from '../../domain/catalogErrors';
import { OperationalChipSelectRow } from '../../../shared/filters/OperationalChipSelectRow';
import type { SupplierFilterState, SupplierRecord } from '../../domain/catalogSupplierTypes';
import { SUPPLIER_PAYMENT_TERMS } from '../../domain/catalogSupplierTypes';
import { ModalFormSection } from '../../../shared/modals/ModalFormSection';
import { styles } from './CatalogMastersScreen.styles';
import { SupplierMasterCard } from './SupplierMasterCard';
import { SupplierMasterTable } from './SupplierMasterTable';
import { MasterFormModal } from './MasterFormModal';
import { MasterFormField } from './MasterFormField';
import { MastersSectionHeader } from './MastersSectionHeader';
import { useMastersPagination } from './useMastersPagination';
import { MastersListFiltersPanel } from '../../../shared/filters/MastersListFiltersPanel';
import {
  CatalogMasterConfirmModal,
  type CatalogMasterConfirmSpec,
} from './CatalogMasterConfirmModal';
import { MasterDetailModal } from './MasterDetailModal';
import { MastersSectionListShell } from './MastersSectionListShell';
import {
  MASTERS_WIDE_LAYOUT_BREAKPOINT,
  mastersFilterOptions,
  type MasterSectionStats,
} from './mastersSectionShared';

const FILTER_OPTIONS = mastersFilterOptions({ active: 'Activos', inactive: 'Inactivos', all: 'Todos' });
const LIST_NOUNS = { singular: 'proveedor', plural: 'proveedores' };

/** Campos del draft con validación inline. */
type SupplierFieldKey =
  | 'name'
  | 'code'
  | 'contactEmail'
  | 'website'
  | 'leadTimeDays'
  | 'minimumOrderAmount';

export type SuppliersMasterSectionProps = {
  canEdit: boolean;
  stats?: MasterSectionStats | null;
  onOpenCsv?: () => void;
  onRecordsChanged?: () => void;
  onBindReload?: (reload: () => void) => void;
};

type DraftState = {
  mode: 'create' | 'edit';
  id?: string;
  name: string;
  code: string;
  taxId: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  paymentTerms: string;
  leadTimeDays: string;
  minimumOrderAmount: string;
  website: string;
  notes: string;
  accountNumber: string;
};

const emptyDraft = (): DraftState => ({
  mode: 'create',
  name: '',
  code: '',
  taxId: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  paymentTerms: '',
  leadTimeDays: '',
  minimumOrderAmount: '',
  website: '',
  notes: '',
  accountNumber: '',
});

export const SuppliersMasterSection = ({
  canEdit,
  stats,
  onOpenCsv,
  onRecordsChanged,
  onBindReload,
}: SuppliersMasterSectionProps) => {
  const { width } = useWindowDimensions();
  const isWide = width >= MASTERS_WIDE_LAYOUT_BREAKPOINT;
  const formScrollContentRef = useRef<View>(null);
  const zipInputRef = useRef<TextInput>(null);
  const notesInputRef = useRef<TextInput>(null);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<DraftState>(() => emptyDraft());
  const [filterState, setFilterState] = useState<SupplierFilterState>('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingMode, setEditingMode] = useState<boolean>(false);
  const [masterConfirmSpec, setMasterConfirmSpec] = useState<CatalogMasterConfirmSpec | null>(null);
  const [detailSupplier, setDetailSupplier] = useState<SupplierRecord | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<SupplierFieldKey, string>>>({});

  const updateDraft = useCallback(
    <K extends keyof DraftState>(key: K, value: DraftState[K]) => {
      setDraft((prev) => ({ ...prev, [key]: value }));
      setFieldErrors((prev) =>
        prev[key as SupplierFieldKey] ? { ...prev, [key as SupplierFieldKey]: undefined } : prev,
      );
    },
    [],
  );

  const {
    items: suppliers,
    pageIndex,
    pageSize,
    totalCount,
    hasMore,
    loading: paginationLoading,
    error: paginationError,
    setPageSize,
    nextPage,
    previousPage,
    reload,
  } = useMastersPagination<SupplierRecord>({
    fetchCount: countSuppliersLocal,
    fetchPage: listSuppliersLocalPaged,
    filterState,
    searchQuery,
    storageKey: 'suppliers_page_size',
  });

  useEffect(() => {
    onBindReload?.(reload);
    return () => onBindReload?.(() => {});
  }, [reload, onBindReload]);

  const onSave = useCallback(async () => {
    // Validación inline: el error queda al lado del campo, no solo en un toast.
    const nextErrors: Partial<Record<SupplierFieldKey, string>> = {};

    const name = draft.name.trim();
    if (!name) {
      nextErrors.name = 'Ingresá un nombre para el proveedor.';
    }

    const taxId = draft.taxId.trim() || null;
    const code = draft.code.trim() || null;
    const contactName = draft.contactName.trim() || null;
    const contactEmail = draft.contactEmail.trim() || null;
    const contactPhone = draft.contactPhone.trim() || null;
    const address = draft.address.trim() || null;
    const city = draft.city.trim() || null;
    const state = draft.state.trim() || null;
    const zip = draft.zip.trim() || null;
    const paymentTerms = draft.paymentTerms.trim() || null;

    // Validar lead time (entero positivo)
    let leadTimeDays: number | null = null;
    if (draft.leadTimeDays.trim()) {
      const days = parseInt(draft.leadTimeDays.trim(), 10);
      if (isNaN(days) || days < 0 || days > 999) {
        nextErrors.leadTimeDays = 'Ingresá un número entero entre 0 y 999.';
      } else {
        leadTimeDays = days;
      }
    }

    // Validar monto mínimo (positivo)
    let minimumOrderAmount: number | null = null;
    if (draft.minimumOrderAmount.trim()) {
      const amount = parseDecimalInput(draft.minimumOrderAmount.trim(), { min: 0 });
      if (amount === null) {
        nextErrors.minimumOrderAmount = 'Ingresá un monto válido (ej. 1500 o 1500,50).';
      } else {
        minimumOrderAmount = amount;
      }
    }

    const website = draft.website.trim() || null;
    const notes = draft.notes.trim() || null;
    const accountNumber = draft.accountNumber.trim() || null;

    // Validar email si se proporciona
    if (contactEmail && !contactEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      nextErrors.contactEmail = 'El email del contacto no es válido.';
    }

    // Validar website si se proporciona
    if (website && !website.match(/^https?:\/\/.+/i)) {
      nextErrors.website = 'El sitio web debe comenzar con http:// o https://';
    }

    setFieldErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    setSaving(true);
    try {
      const params = {
        name,
        code,
        taxId,
        contactName,
        contactEmail,
        contactPhone,
        address,
        city,
        state,
        zip,
        paymentTerms,
        leadTimeDays,
        minimumOrderAmount,
        website,
        notes,
        accountNumber,
      };
      if (draft.mode === 'edit' && draft.id) {
        await updateSupplier({ id: draft.id, ...params });
        showSuccess('Proveedor actualizado', 'Los cambios se guardaron.');
      } else {
        await createSupplier(params);
        showSuccess('Proveedor creado', 'Ya podés asignarlo a productos.');
      }
      setDraft(emptyDraft());
      setFieldErrors({});
      setEditingMode(false);
      reload();
      onRecordsChanged?.();
    } catch (error: unknown) {
      if (error instanceof SupplierCodeDuplicateError) {
        // Código duplicado: error inline en el campo, el modal queda abierto.
        setFieldErrors((prev) => ({ ...prev, code: error.message }));
        return;
      }
      if (error instanceof CatalogDomainError) {
        showError('No se pudo guardar el proveedor', error.message);
        return;
      }
      presentUnexpectedFailure({ module: 'catalog', op: 'supplier.save', error, userMessage: 'No se pudo guardar el proveedor.' });
    } finally {
      setSaving(false);
    }
  }, [draft, reload, onRecordsChanged, setEditingMode]);

  const onEdit = useCallback((supplier: SupplierRecord) => {
    setDraft({
      mode: 'edit',
      id: supplier.id,
      name: supplier.name,
      code: supplier.code ?? '',
      taxId: supplier.taxId ?? '',
      contactName: supplier.contactName ?? '',
      contactEmail: supplier.contactEmail ?? '',
      contactPhone: nationalPartFromStored(supplier.contactPhone ?? ''),
      address: supplier.address ?? '',
      city: supplier.city ?? '',
      state: supplier.state ?? '',
      zip: supplier.zip ?? '',
      paymentTerms: supplier.paymentTerms ?? '',
      leadTimeDays: supplier.leadTimeDays?.toString() ?? '',
      minimumOrderAmount: supplier.minimumOrderAmount?.toString() ?? '',
      website: supplier.website ?? '',
      notes: supplier.notes ?? '',
      accountNumber: supplier.accountNumber ?? '',
    });
    setFieldErrors({});
    setEditingMode(true);
  }, []);

  const onCancelEdit = useCallback(() => {
    setDraft(emptyDraft());
    setFieldErrors({});
    setEditingMode(false);
  }, []);

  const onStartCreate = useCallback(() => {
    setDraft(emptyDraft());
    setFieldErrors({});
    setEditingMode(true);
  }, []);

  const applySupplierActive = useCallback(
    async (supplier: SupplierRecord, nextActive: boolean) => {
      setSaving(true);
      try {
        await setSupplierActive({ id: supplier.id, isActive: nextActive });
        reload();
        onRecordsChanged?.();
        showSuccess(
          nextActive ? 'Proveedor activado' : 'Proveedor desactivado',
          nextActive ? 'Volvió a aparecer en selectores.' : 'Ya no aparece en selectores.'
        );
      } catch (error: any) {
        presentUnexpectedFailure({ module: 'catalog', op: 'no-pudimos-cambiar-el-estado', error: error, title: 'No pudimos cambiar el estado', userMessage: 'Ocurrió un error' });
      } finally {
        setSaving(false);
      }
    },
    [reload, onRecordsChanged]
  );

  const onToggleActive = useCallback(
    (supplier: SupplierRecord) => {
      if (supplier.isActive) {
        setMasterConfirmSpec({
          kind: 'confirm',
          title: 'Desactivar proveedor',
          entityName: supplier.name,
          message:
            'Deja de aparecer en los selectores. Lo podés volver a mostrar desde Inactivos.',
          confirmLabel: 'Desactivar',
          iconName: 'eye-off-outline',
          onConfirm: async () => {
            setMasterConfirmSpec(null);
            await applySupplierActive(supplier, false);
          },
        });
        return;
      }
      void applySupplierActive(supplier, true);
    },
    [applySupplierActive]
  );

  const onOpenDetail = useCallback((supplier: SupplierRecord) => {
    setDetailSupplier(supplier);
  }, []);

  const onCloseDetail = useCallback(() => {
    setDetailSupplier(null);
  }, []);

  return (
    <View style={{ gap: spacing.lg }}>
      <MastersListFiltersPanel
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        searchLabel="Buscar proveedores"
        searchPlaceholder="Buscar por nombre o código"
        stateOptions={FILTER_OPTIONS.map((opt) => ({ value: opt.id, label: opt.label }))}
        stateCounts={stats ?? undefined}
        filterState={filterState}
        onFilterStateChange={setFilterState}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        layoutWide={isWide}
        trailingActions={
          <MastersSectionHeader
            canEdit={canEdit}
            editingMode={editingMode}
            csvLabel="CSV"
            createLabel="Agregar proveedor"
            onOpenCsv={onOpenCsv}
            onCreate={onStartCreate}
          />
        }
      />

      <MastersSectionListShell
        loading={paginationLoading}
        error={paginationError}
        itemCount={suppliers.length}
        totalCount={totalCount}
        pageIndex={pageIndex}
        pageSize={pageSize}
        hasMore={hasMore}
        listNouns={LIST_NOUNS}
        loadingLabel="Cargando proveedores…"
        emptyIcon="business-outline"
        emptyTitle="Sin proveedores"
        emptyErrorTitle="No pudimos cargar proveedores"
        emptyMessage="Creá el primero para asignarlo a productos."
        canEdit={canEdit}
        createLabel="Agregar"
        onCreate={onStartCreate}
        onRetry={reload}
        isWide={isWide}
        onPreviousPage={previousPage}
        onNextPage={nextPage}
        onPageSizeChange={setPageSize}
        renderWide={() => <SupplierMasterTable suppliers={suppliers} onOpenDetail={onOpenDetail} />}
        renderMobile={() =>
          suppliers.map((supplier) => (
            <SupplierMasterCard
              key={supplier.id}
              supplier={supplier}
              onPress={() => onOpenDetail(supplier)}
            />
          ))
        }
      />

      <CatalogMasterConfirmModal
        spec={masterConfirmSpec}
        onClose={() => {
          if (!saving) setMasterConfirmSpec(null);
        }}
      />

      <MasterFormModal
        visible={canEdit && editingMode}
        mode={draft.mode}
        entityKicker="Proveedor"
        entityName={draft.name}
        headerTitle={draft.mode === 'edit' ? 'Editar proveedor' : 'Nuevo proveedor'}
        saving={saving}
        saveLabel={draft.mode === 'edit' ? 'Guardar' : 'Crear proveedor'}
        scrollContentRef={formScrollContentRef}
        onSave={onSave}
        onClose={onCancelEdit}
      >
        <ModalFormSection title="Datos">
          <MasterFormField label="Razón social o nombre" error={fieldErrors.name}>
          <TextInput
            style={[styles.formInput, fieldErrors.name ? styles.formInputError : null]}
            value={draft.name}
            onChangeText={(value) => updateDraft('name', value)}
            placeholder="Nombre comercial"
            placeholderTextColor={ticketColors.ink3}
            autoCapitalize="words"
            editable={!saving}
            accessibilityLabel="Razón social o nombre del proveedor"
          />
          </MasterFormField>
          <MasterFormField label="Código interno" error={fieldErrors.code}>
          <TextInput
            style={[styles.formInput, fieldErrors.code ? styles.formInputError : null]}
            value={draft.code}
            onChangeText={(value) => updateDraft('code', value)}
            placeholder="Opcional"
            placeholderTextColor={ticketColors.ink3}
            autoCapitalize="characters"
            editable={!saving}
            accessibilityLabel="Código interno del proveedor"
          />
          </MasterFormField>
          <MasterFormField label="CUIT">
          <NumericTextInput
            kind="digits"
            style={styles.formInput}
            value={draft.taxId}
            onChangeText={(value) => updateDraft('taxId', value)}
            placeholder="Opcional"
            placeholderTextColor={ticketColors.ink3}
            editable={!saving}
            accessibilityLabel="CUIT del proveedor"
          />
          </MasterFormField>
          <MasterFormField label="Número de cuenta cliente">
          <TextInput
            style={styles.formInput}
            value={draft.accountNumber}
            onChangeText={(value) => updateDraft('accountNumber', value)}
            placeholder="Opcional"
            placeholderTextColor={ticketColors.ink3}
            editable={!saving}
            accessibilityLabel="Número de cuenta cliente"
          />
          </MasterFormField>
        </ModalFormSection>

        <ModalFormSection title="Contacto" divided>
          <MasterFormField label="Nombre del contacto">
          <TextInput
            style={styles.formInput}
            value={draft.contactName}
            onChangeText={(value) => updateDraft('contactName', value)}
            placeholder="Opcional"
            placeholderTextColor={ticketColors.ink3}
            autoCapitalize="words"
            editable={!saving}
            accessibilityLabel="Nombre del contacto"
          />
          </MasterFormField>
          <MasterFormField label="Email del contacto" error={fieldErrors.contactEmail}>
          <TextInput
            style={[styles.formInput, fieldErrors.contactEmail ? styles.formInputError : null]}
            value={draft.contactEmail}
            onChangeText={(value) => updateDraft('contactEmail', value)}
            placeholder="Opcional"
            placeholderTextColor={ticketColors.ink3}
            keyboardType="email-address"
            autoCapitalize="none"
            autoCorrect={false}
            editable={!saving}
            accessibilityLabel="Email del contacto"
          />
          </MasterFormField>
          <MasterFormField label="Teléfono del contacto">
          <NumericTextInput
            kind="phone"
            style={styles.formInput}
            value={draft.contactPhone}
            onChangeText={(value) => updateDraft('contactPhone', value)}
            placeholder="11 5555-5555"
            placeholderTextColor={ticketColors.ink3}
            editable={!saving}
            accessibilityLabel="Teléfono del contacto"
          />
          </MasterFormField>
          <MasterFormField label="Sitio web" error={fieldErrors.website}>
          <TextInput
            style={[styles.formInput, fieldErrors.website ? styles.formInputError : null]}
            value={draft.website}
            onChangeText={(value) => updateDraft('website', value)}
            placeholder="https://..."
            placeholderTextColor={ticketColors.ink3}
            keyboardType="url"
            autoCapitalize="none"
            autoCorrect={false}
            editable={!saving}
            accessibilityLabel="Sitio web del proveedor"
          />
          </MasterFormField>
        </ModalFormSection>

        <ModalFormSection title="Dirección" divided>
          <MasterFormField label="Dirección">
          <TextInput
            style={styles.formInput}
            value={draft.address}
            onChangeText={(value) => updateDraft('address', value)}
            placeholder="Opcional"
            placeholderTextColor={ticketColors.ink3}
            autoCapitalize="words"
            editable={!saving}
            accessibilityLabel="Dirección del proveedor"
          />
          </MasterFormField>
          <MasterFormField label="Ciudad">
          <TextInput
            style={styles.formInput}
            value={draft.city}
            onChangeText={(value) => updateDraft('city', value)}
            placeholder="Opcional"
            placeholderTextColor={ticketColors.ink3}
            autoCapitalize="words"
            editable={!saving}
            accessibilityLabel="Ciudad del proveedor"
          />
          </MasterFormField>
          <MasterFormField label="Provincia">
          <TextInput
            style={styles.formInput}
            value={draft.state}
            onChangeText={(value) => updateDraft('state', value)}
            placeholder="Opcional"
            placeholderTextColor={ticketColors.ink3}
            autoCapitalize="words"
            editable={!saving}
            accessibilityLabel="Provincia del proveedor"
          />
          </MasterFormField>
          <MasterFormField label="Código postal">
          <TextInput
            ref={zipInputRef}
            style={styles.formInput}
            value={draft.zip}
            onChangeText={(value) => updateDraft('zip', value)}
            placeholder="Opcional"
            placeholderTextColor={ticketColors.ink3}
            editable={!saving}
            accessibilityLabel="Código postal del proveedor"
          />
          </MasterFormField>
        </ModalFormSection>

        <ModalFormSection title="Pago y entrega" divided>
          <MasterFormField label="Plazo de pago">
          <OperationalChipSelectRow
            value={draft.paymentTerms}
            onChange={(value) => updateDraft('paymentTerms', value)}
            options={[
              { value: '', label: 'Sin definir' },
              ...SUPPLIER_PAYMENT_TERMS.map((t) => ({ value: t.value as string, label: t.label })),
            ]}
          />
          </MasterFormField>
          <MasterFormField label="Días de entrega" error={fieldErrors.leadTimeDays}>
          <NumericTextInput
            kind="integer"
            style={[styles.formInput, fieldErrors.leadTimeDays ? styles.formInputError : null]}
            value={draft.leadTimeDays}
            onChangeText={(value) => updateDraft('leadTimeDays', value)}
            placeholder="Ej: 7"
            placeholderTextColor={ticketColors.ink3}
            editable={!saving}
            accessibilityLabel="Días de entrega del proveedor"
          />
          </MasterFormField>
          <MasterFormField label="Pedido mínimo" error={fieldErrors.minimumOrderAmount}>
          <NumericTextInput
            kind="decimalMoney"
            style={[styles.formInput, fieldErrors.minimumOrderAmount ? styles.formInputError : null]}
            value={draft.minimumOrderAmount}
            onChangeText={(value) => updateDraft('minimumOrderAmount', value)}
            placeholder="Opcional"
            placeholderTextColor={ticketColors.ink3}
            editable={!saving}
            accessibilityLabel="Monto de pedido mínimo"
          />
          </MasterFormField>
        </ModalFormSection>

        <ModalFormSection title="Notas internas" divided>
          <MasterFormField label="Notas">
          <TextInput
            ref={notesInputRef}
            style={[styles.formInput, { minHeight: 88 }]}
            value={draft.notes}
            onChangeText={(value) => updateDraft('notes', value)}
            placeholder="Opcional"
            placeholderTextColor={ticketColors.ink3}
            accessibilityLabel="Notas internas del proveedor"
            multiline
            numberOfLines={3}
            editable={!saving}
          />
          </MasterFormField>
        </ModalFormSection>
      </MasterFormModal>

      <MasterDetailModal
        visible={!!detailSupplier}
        entityType="supplier"
        entity={detailSupplier!}
        canEdit={canEdit}
        onClose={onCloseDetail}
        onEdit={() => {
          if (detailSupplier) {
            onCloseDetail();
            onEdit(detailSupplier);
          }
        }}
        onToggleActive={() => {
          if (detailSupplier) {
            onCloseDetail();
            onToggleActive(detailSupplier);
          }
        }}
      />
    </View>
  );
};
